import React, { ReactElement, useState } from 'react';
import { Loader } from 'idsk-react-components';
import { TypCiselnikaEnum } from 'ciselniky-frontend-ts-axios';
import { ExpansionPanel, MultiSelect, TreeMultiPicker } from 'slovlex-idsk-react-components';
import PolozkaCiselnik from '../../model/ciselniky/PolozkaCiselnik';
import { SafeParameterSetter } from '../../types/SafeParameterSetter';
import useSearchValidPolozkyCiselnik from '../../hooks/useSearchValidPolozkyCiselnik';
import useHierarchickyCiselnikTree from './hook/useHierarchickyCiselnikTree';
import { SHOW_ALL_MAX_RESULT, MAX_MULTISELECT_RESULT } from '../../constants/MultiSelectConstant';

interface Props<T> {
  oblastList: PolozkaCiselnik[];
  onUpdateField: SafeParameterSetter<T>;
  label?: string,
  hint?: string,
  getFieldErrorMessage?: (fieldName: keyof T) => string;
  fazet?: boolean;
  showItemList?: boolean;
  optional?: boolean;
  isExpanded?: boolean;
}

interface SelectedItem {
  kod: string;
  nazov: string;
  uuid: string;
}

const mapOblastToSelectedItem = (oblast: PolozkaCiselnik): SelectedItem => ({
  kod: oblast.kod,
  uuid: oblast.uuid,
  nazov: oblast.nazov,
});

const MultiSelectWithTreeForOblast = <T extends { oblastList: PolozkaCiselnik[] }, >({
  label,
  hint,
  onUpdateField,
  oblastList,
  getFieldErrorMessage,
  fazet = false,
  showItemList = true,
  optional = false, isExpanded = oblastList?.length > 0,
}: Props<T>): ReactElement => {
  const [searchText, setSearchText] = useState('');
  const [limit, setLimit] = useState(MAX_MULTISELECT_RESULT);
  const [execute, setExecute] = useState(oblastList?.length > 0 || showItemList);

  const {
    polozkyCiselnik: oblastFilteredList,
    polozkyCiselnikStatus,
  } = useSearchValidPolozkyCiselnik(
    {
      kody: [],
      typy: [TypCiselnikaEnum.LegislativnaOblast],
      ids: [],
      platny: true,
      hladanyNazov: searchText,
      limit,
      execute,
    },
  );

  const {
    hierarchickyCiselnikTreeList,
    hierarchickyCiselnikTreeStatus,
  } = useHierarchickyCiselnikTree(
    TypCiselnikaEnum.LegislativnaOblast,
    execute,
  );
  const selectedItems = oblastList ? oblastList.map(mapOblastToSelectedItem) : [];

  const renderMultiSelect = ():ReactElement => (
    <MultiSelect
      loading={polozkyCiselnikStatus.loading}
      handleShowAll={() => {
        setLimit(SHOW_ALL_MAX_RESULT);
      }}
      withShowAllButton={(limit === MAX_MULTISELECT_RESULT
          && oblastFilteredList.length === MAX_MULTISELECT_RESULT)
        || (limit === SHOW_ALL_MAX_RESULT && oblastFilteredList.length <= MAX_MULTISELECT_RESULT)}
      searchText={searchText}
      setSearchText={(text) => {
        setSearchText(text);
        setLimit(MAX_MULTISELECT_RESULT);
      }}
      label={label}
      hint={hint}
      error={getFieldErrorMessage && getFieldErrorMessage('oblastList')}
      data={oblastFilteredList}
      dataAttributesToShow={[{ key: 'nazov', label: '' }]}
      selectedItemList={selectedItems}
      setSelectedItemList={(selected: PolozkaCiselnik[]) => {
        onUpdateField('oblastList', selected);
      }}
      maxResults={SHOW_ALL_MAX_RESULT}
      fazet={fazet}
      itemListAttribute="nazov"
      showItemList={showItemList}
      required={!optional}
      aria-label="Právna oblasť"
    >
      <Loader loading={hierarchickyCiselnikTreeStatus.loading}>
        <TreeMultiPicker
          onAddItems={items => {
            const treePickerItems = items as Array<PolozkaCiselnik>;

            const filterRemovedItems = oblastList.filter(
              (item) => treePickerItems.some((selected) => selected.uuid === item.uuid),
            );

            const uniqueTreePickerItems = treePickerItems.filter(item => !oblastList
              .includes(oblastList.find(selected => selected.uuid === item.uuid)));
            onUpdateField(
              'oblastList',
              [...filterRemovedItems, ...uniqueTreePickerItems],
            );
          }}
          selectedItems={selectedItems}
          data={hierarchickyCiselnikTreeList}
        />
      </Loader>
    </MultiSelect>
  );

  return !showItemList ? (
    <ExpansionPanel
      title="Právna oblasť"
      isExpanded={isExpanded}
      onExpand={() => setExecute(true)}
    >
      {renderMultiSelect()}
    </ExpansionPanel>
  ) : renderMultiSelect();
};

export default MultiSelectWithTreeForOblast;
